import { useCallback, useEffect, useRef, useState } from 'react'
import type { GameViewProps } from '../../core/contracts'
import { haptics } from '../../platform/telegram'
import { useT } from '../../platform/i18n/useT'
import { angleOf } from './logic'
import type { TargetAnswer, TargetRound } from './logic'
import './view.css'

/**
 * Экран Reaction Target.
 *
 * Поле стрелок, одна повёрнута иначе. Проходим блок проб по одной,
 * ответы копим и отдаём движку пакетом.
 */

export function ReactionTargetView({
  round,
  level,
  onAnswer,
}: GameViewProps<TargetRound, TargetAnswer>) {
  const { t } = useT()
  const [index, setIndex] = useState(0)
  const [flash, setFlash] = useState<'hit' | 'miss' | null>(null)

  const picks = useRef<(number | null)[]>([])
  const times = useRef<number[]>([])
  const shownAt = useRef(performance.now())
  const locked = useRef(false)

  const trial = round.trials[index]

  const finish = useCallback(() => {
    const ok = times.current
    const rtMs =
      ok.length > 0
        ? Math.round(ok.reduce((a, b) => a + b, 0) / ok.length)
        : round.timeLimitMs
    onAnswer({ picks: picks.current, rtMs })
  }, [onAnswer, round.timeLimitMs])

  const submit = useCallback(
    (pick: number | null) => {
      if (locked.current || !trial) return
      locked.current = true

      const rt = performance.now() - shownAt.current
      const hit = pick === trial.targetIndex

      picks.current.push(pick)
      // время только по верным ответам: время промаха не отражает
      // скорость поиска
      if (hit) times.current.push(rt)

      if (hit) haptics.correct()
      else haptics.wrong()
      setFlash(hit ? 'hit' : 'miss')

      setTimeout(() => {
        setFlash(null)
        locked.current = false
        if (index + 1 >= round.trials.length) {
          finish()
        } else {
          shownAt.current = performance.now()
          setIndex((i) => i + 1)
        }
      }, 220)
    },
    [trial, index, round.trials.length, finish],
  )

  useEffect(() => {
    shownAt.current = performance.now()
    const id = setTimeout(() => submit(null), round.timeLimitMs)
    return () => clearTimeout(id)
  }, [index, round.timeLimitMs, submit])

  if (!trial) return null

  const cells = Array.from({ length: trial.gridSize * trial.gridSize }, (_, i) => i)

  return (
    <div className="rt">
      <div className="rt__top">
        <span className="rt__level">{t('game.level', { n: level })}</span>
        <span className="rt__count">
          {index + 1} / {round.trials.length}
        </span>
      </div>

      <div className="rt__timer" aria-hidden="true">
        <div
          key={index}
          className="rt__timer-fill"
          style={{ animationDuration: `${round.timeLimitMs}ms` }}
        />
      </div>

      <p className="rt__hint">{t('game.reactionTarget.hint')}</p>

      <div
        className={flash ? `rt__field rt__field--${flash}` : 'rt__field'}
        style={{ gridTemplateColumns: `repeat(${trial.gridSize}, 1fr)` }}
        role="group"
        aria-label={t('game.reactionTarget')}
      >
        {cells.map((cell) => (
          <button
            key={cell}
            type="button"
            className="rt__cell"
            onClick={() => submit(cell)}
            aria-label={`${cell + 1}`}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 3.5 19 17.5H5Z"
                fill="currentColor"
                transform={`rotate(${angleOf(trial, cell)} 12 12)`}
              />
            </svg>
          </button>
        ))}
      </div>
    </div>
  )
}
